// Accordion section. Three states render the SAME DOM tree with
// state-driven class variants so CSS transitions can animate between
// them smoothly:
//   - locked:    flat tinted panel, muted title, body collapsed
//   - active:    white card with primary border, body expanded
//   - completed: white card with title + summary + Edit pill
//
// Step number badges are intentionally not rendered — the section title
// + scroll position already convey progress, and dropping the badge
// gives the layout more breathing room.
//
// Children mount on first active state and stay mounted across
// completed/active toggles. That keeps internal step state (form inputs,
// preview cache, etc.) alive when the user clicks Edit on a completed
// section. They never mount while locked.

import { useEffect, useRef, useState } from 'react';

export type SectionState = 'locked' | 'active' | 'completed';

interface SectionProps {
  /** 1-indexed step number — used only for aria-label / a11y today;
   *  no longer rendered in the header (per mockup). */
  step: number;
  /** Section heading (e.g. "Platform") */
  title: string;
  /** Remix Icon class (e.g. 'ri-smartphone-line'). Rendered as a small
   *  primary-tinted tile to the left of the title in the active state.
   *  Mirrors the per-step icons used in /app's StepHeader so users get
   *  a visual anchor of which step they're on. */
  icon?: string;
  /** Optional one-line description shown under the title when active */
  description?: string;
  /** Optional summary text shown in the collapsed completed row */
  summary?: string;
  state: SectionState;
  /** Fired from the Edit pill on completed sections */
  onEdit?: () => void;
  /** Footer slot inside the active card — typically the Continue button.
   *  Rendered with a separator above so it reads as a confirm step,
   *  regardless of what the children laid out above it. Sections that
   *  own their own footer (Preview, Add Text) omit this. */
  cta?: React.ReactNode;
  /** When true (and used inside a flex-col parent), the section's outer
   *  shell + body grow to fill remaining vertical space instead of taking
   *  natural content height. Used in /appv2/recreate/:id to make Section 2
   *  match the LEFT-pane image height. Side effect: drops the section's
   *  mb-4 bottom margin since flex-1 implies the section IS the last
   *  thing in its column. */
  fill?: boolean;
  children?: React.ReactNode;
}

// Match the body's grid-rows animation duration. We delay scrollIntoView
// by this amount so layout is settled before we snap the viewport.
const TRANSITION_MS = 300;

export default function Section({
  step,
  title,
  icon,
  description,
  summary,
  state,
  onEdit,
  cta,
  fill,
  children,
}: SectionProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Lazy mount: once a section has been active, its children stay in the
  // DOM so the collapse animation has content to animate and so step
  // state survives an Edit round-trip.
  const [hasBeenActive, setHasBeenActive] = useState(state === 'active');
  useEffect(() => {
    if (state === 'active') setHasBeenActive(true);
  }, [state]);

  // Scroll the section to the top of the viewport AFTER the expand
  // animation finishes. If we scroll on the same tick the state changes,
  // the previously-active section is still collapsing and the new
  // section is still expanding — layout is mid-flight, so the scroll
  // target ends up wrong (sometimes too high, sometimes too low). Wait
  // out the transition for a stable result.
  useEffect(() => {
    if (state !== 'active' || !ref.current) return;
    const id = window.setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, TRANSITION_MS + 30);
    return () => window.clearTimeout(id);
  }, [state]);

  const shellClasses = (() => {
    // `fill` (used by /appv2/recreate/:id's Section 2) makes the section
    // a flex-1 column so it stretches to match its sibling's height.
    // Drops the bottom margin since a flex-1 child is the column-anchor.
    const sizing = fill ? 'flex-1 min-h-0 flex flex-col' : 'mb-4';
    const base = `rounded-2xl ${sizing} transition-all duration-300 ease-out scroll-mt-24`;
    if (state === 'active') {
      // TRIAL: full glassmorphism on the active panel — heavy backdrop
      // blur + low-opacity white so the gradient mesh shows through as
      // a soft frosted color wash. Pink border + shadow keep the
      // "where you are now" signal strong. If perf or readability
      // suffers, revert to `bg-white/75` (no backdrop-blur) — the rest
      // of the polish stack stands without it.
      return `${base} border-2 border-primary bg-white/55 backdrop-blur-2xl shadow-xl shadow-primary/15`;
    }
    if (state === 'completed') {
      // glass-stroke pseudo replaces a solid white border with a
      // diagonal white→transparent→white gradient — opposite corners
      // catch light, the perpendicular edges fade out. Subtle hover
      // lift for the "click to edit" affordance.
      return `${base} glass-stroke bg-white/85 shadow-md shadow-primary/5 hover:bg-white hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/10`;
    }
    // Locked — same gradient stroke for visual family. Tinted bg + no
    // shadow so locked tiles recede compared to completed ones.
    return `${base} glass-stroke bg-surface-panel/85`;
  })();

  return (
    <div
      ref={ref}
      className={shellClasses}
      aria-label={`Step ${step}: ${title}`}
    >
      {/* HEADER ROW — title + sub-line + right-side affordance. No step
          badge per mockup; section is identified by its title alone.
          When completed, the ENTIRE header row is a click-to-edit target
          (rendered as a <button>) so users don't have to aim for the
          tiny Edit pill. The pill is kept as a visual affordance hint. */}
      {state === 'completed' && onEdit ? (
        <button
          type="button"
          onClick={onEdit}
          className="w-full text-left px-6 py-5 flex items-center gap-3 transition-colors duration-300 rounded-2xl cursor-pointer group"
          aria-label={`Edit ${title}`}
        >
          <div className="flex-1 min-w-0">
            <h3 className="font-display transition-all duration-300 font-bold text-dark text-base group-hover:text-primary">
              {title}
            </h3>
            {summary && (
              <p className="text-xs text-dark-muted truncate mt-0.5">{summary}</p>
            )}
          </div>
          {/* Edit pill kept as a discoverability hint — visually says
              "click anywhere on this row, here's the action". */}
          <div className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-surface-dark/40 text-dark-muted group-hover:border-primary/40 group-hover:text-primary group-hover:bg-primary/5 text-xs font-semibold transition-colors">
            <i className="ri-pencil-line text-xs" />
            Edit
          </div>
        </button>
      ) : (
        <div
          className={`px-6 py-5 flex items-center gap-3 transition-colors duration-300 ${
            state === 'active' ? 'border-b border-surface-dark/20' : ''
          }`}
        >
          {/* Icon tile — active state only. Mirrors the StepHeader icon
              treatment from /app so the per-step visual anchor is the same. */}
          {state === 'active' && icon && (
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <i className={`${icon} text-xl`} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3
              className={`font-display transition-all duration-300 ${
                state === 'active'
                  ? 'font-bold text-dark text-lg'
                  : 'font-semibold text-dark-muted/70 text-base'
              }`}
            >
              {title}
            </h3>

            {state === 'active' && description && (
              <p className="text-sm text-dark-muted mt-1">{description}</p>
            )}
          </div>
        </div>
      )}

      {/* BODY — animated collapse via grid-template-rows 0fr ↔ 1fr.
          Inner div needs overflow-hidden so content clips during transit.
          `inert` (not aria-hidden) — the children stay mounted across
          completed/active toggles, so any focused input/button inside
          would trigger Chrome's "aria-hidden on focused descendant"
          warning. `inert` properly removes both focus AND a11y access
          for collapsed states.

          When `fill` + active, the body becomes a flex column that
          stretches inside the outer flex-1 shell. The inner content area
          gets h-full + flex-col + cta justify-self-end so the CTA (if
          any) anchors to the bottom and the children fill the space
          above. When NOT filling, the original natural-height layout
          stays untouched. */}
      <div
        className={`${
          fill && state === 'active' ? 'flex-1 min-h-0 flex flex-col' : ''
        } grid transition-[grid-template-rows] duration-300 ease-out ${
          state === 'active' ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
        inert={state !== 'active'}
      >
        <div className={`overflow-hidden ${fill && state === 'active' ? 'flex flex-col min-h-0' : ''}`}>
          <div className={`px-6 pb-6 pt-2 ${fill && state === 'active' ? 'flex flex-col flex-1 min-h-0' : ''}`}>
            <div className={fill && state === 'active' ? 'flex-1 min-h-0' : ''}>
              {hasBeenActive ? children : null}
            </div>
            {cta && state === 'active' && (
              // Centered (not justify-end) since there's no Back button
              // pairing with it — a lone right-aligned CTA reads like
              // "half-empty footer". Centered claims the whole footer
              // band as confirmation space.
              <div className="mt-6 pt-5 border-t border-surface-dark/20 flex justify-center flex-shrink-0">
                {cta}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
